import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { NumberInput } from '../../../components/NumberInput';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';
import { validarRutChileno } from '../../../lib/rut';
import { ProveedorCombobox } from '../../../components/compras/ProveedorCombobox';
import { CuentaContableSelect, DocumentosPendientes } from './EgresoCuentaContable';
import { fmtCLP } from './egresosUtils';

const hoyISO = () => new Date().toISOString().slice(0, 10);

const formInicial = (bancoPrincipalId) => ({
  proveedor: null, monto: '', fecha_pago: hoyISO(), banco_id: bancoPrincipalId ? String(bancoPrincipalId) : '',
  banco_destino_id: '', oc_numero: '', descripcion: '', payment_method: 'transfer', numero_cheque: '', crear_en_alegra: true,
});

// Iter 390 (Renzo) — Nuevo egreso: proveedor buscable (RUT módulo 11 vía lib/rut.js), cuenta contable obligatoria,
// documentos pendientes solo lectura cuando la cuenta es CxP proveedores, transferencia entre cuentas propias.
export function EgresoCrearDialog({ open, onOpenChange, bancos, bancoPrincipalId, onCreated }) {
  const [form, setForm] = useState(formInicial(bancoPrincipalId));
  const [cuenta, setCuenta] = useState(null);
  const [billIds, setBillIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const modo = cuenta?.modo || 'anticipo';
  const onCuenta = useCallback((c) => setCuenta(c), []);

  useEffect(() => { if (open) { setForm(formInicial(bancoPrincipalId)); setBillIds([]); } }, [open, bancoPrincipalId]);

  useEffect(() => {
    if (!open || !form.fecha_pago || form.payment_method === 'check') { setPreview(null); return undefined; }
    const tarde = form.fecha_pago !== hoyISO() || new Date().getHours() >= 14;
    const t = setTimeout(() => {
      api.get('/config/calendario-bancario/siguiente-habil', { params: { fecha: form.fecha_pago, tarde } })
        .then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [open, form.fecha_pago, form.payment_method]);

  const handleCreate = async () => {
    if (!cuenta) { toast.error('⛔ Selecciona la cuenta contable'); return; }
    if (!form.fecha_pago) { toast.error('⛔ La fecha de pago es obligatoria'); return; }
    if (!form.banco_id) { toast.error('⛔ Banco / Cuenta de origen es obligatorio'); return; }
    if (modo === 'transferencia') {
      if (!form.banco_destino_id) { toast.error('⛔ Indica la cuenta de destino'); return; }
      if (form.banco_destino_id === form.banco_id) { toast.error('⛔ La cuenta de destino debe ser distinta a la de origen'); return; }
    } else if (!form.proveedor || !validarRutChileno(form.proveedor.rut)) { toast.error('⛔ Selecciona un proveedor válido (RUT módulo 11)'); return; }
    const monto = parseFloat(form.monto);
    if (modo === 'documentos') {
      if (!billIds.length) { toast.error('⛔ Marca al menos un documento pendiente a pagar'); return; }
    } else if (isNaN(monto) || monto <= 0) { toast.error('⛔ Monto inválido'); return; }
    if (form.payment_method === 'check' && !form.numero_cheque.trim()) { toast.error('Indica el número de cheque'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/contabilidad/egresos', {
        rut_proveedor: form.proveedor?.rut || '', razon_social: form.proveedor?.nombre || '',
        monto: modo === 'documentos' ? 0 : monto, fecha_pago: form.fecha_pago,
        banco_id: parseInt(form.banco_id, 10), banco_destino_id: form.banco_destino_id ? parseInt(form.banco_destino_id, 10) : null,
        oc_numero: form.oc_numero, descripcion: form.descripcion, payment_method: form.payment_method, numero_cheque: form.numero_cheque,
        crear_en_alegra: form.crear_en_alegra, cuenta_id: cuenta.id, cuenta_nombre: cuenta.name, bill_ids: billIds,
      });
      const d = res.data || {};
      const eg = d.egreso || {};
      if (d.modo === 'transferencia') toast.success(`Transferencia registrada: egreso ${eg.alegra_number || eg.codigo_conciliacion || ''} + ingreso ${d.ingreso?.alegra_number || ''} en Alegra`);
      else if (d.alegra_synced) toast.success(`Egreso ${eg.alegra_number || eg.codigo_conciliacion || ''} creado en Alegra (${fmtCLP(eg.monto)} · ${cuenta.name})`);
      else toast.success(`Egreso ${eg.codigo_conciliacion || ''} registrado localmente`);
      onOpenChange(false);
      onCreated?.(eg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear egreso', { duration: 8000 });
    } finally { setSubmitting(false); }
  };

  const bancosDestino = bancos.filter((b) => String(b.id) !== form.banco_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="modal-crear-egreso">
        <DialogHeader>
          <DialogTitle>Nuevo egreso</DialogTitle>
          <DialogDescription>Se crea en Alegra con todos los datos requeridos (proveedor, banco, cuenta contable) y queda pendiente de conciliar con la cartola.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <CuentaContableSelect value={cuenta} onChange={onCuenta} />
          <div>
            <Label className="text-xs">Fecha pago *</Label>
            <Input type="date" value={form.fecha_pago} onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })} className="text-sm" data-testid="input-fecha" />
          </div>
          {modo !== 'transferencia' ? (
            <div className="col-span-2">
              <Label className="text-xs">Proveedor * (busca por RUT o nombre)</Label>
              <ProveedorCombobox value={form.proveedor?.rut || ''} onChange={(p) => setForm({ ...form, proveedor: p })} testid="input-proveedor" />
              {form.proveedor && (
                <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="proveedor-seleccionado">
                  RUT <b className="font-mono">{form.proveedor.rut}</b> · {form.proveedor.nombre} · Alegra #{form.proveedor.alegra_id}
                </p>
              )}
            </div>
          ) : (
            <div className="col-span-2">
              <Label className="text-xs">Cuenta de destino * (donde ingresa el dinero)</Label>
              <Select value={form.banco_destino_id} onValueChange={(v) => setForm({ ...form, banco_destino_id: v })}>
                <SelectTrigger className="text-sm" data-testid="select-banco-destino"><SelectValue placeholder="Selecciona la cuenta destino" /></SelectTrigger>
                <SelectContent>{bancosDestino.map((b) => <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {modo === 'documentos' ? (
            <DocumentosPendientes rut={form.proveedor?.rut || ''} seleccion={billIds} onSeleccion={setBillIds} />
          ) : (
            <>
              <div>
                <Label className="text-xs">Monto (CLP) *</Label>
                <NumberInput placeholder="500000" value={form.monto} onChange={(v) => setForm({ ...form, monto: v })} className="text-sm tabular-nums" data-testid="input-monto" />
              </div>
              {modo !== 'transferencia' && (
                <div>
                  <Label className="text-xs">OC asociada (opcional)</Label>
                  <Input placeholder="18615" value={form.oc_numero} onChange={(e) => setForm({ ...form, oc_numero: e.target.value })} className="text-sm" data-testid="input-oc" />
                </div>
              )}
            </>
          )}
          <div>
            <Label className="text-xs">Banco / Cuenta de origen *</Label>
            <Select value={form.banco_id} onValueChange={(v) => setForm({ ...form, banco_id: v })}>
              <SelectTrigger className={`text-sm ${!form.banco_id ? 'border-red-400' : ''}`} data-testid="select-banco"><SelectValue placeholder="Selecciona el banco" /></SelectTrigger>
              <SelectContent>
                {bancos.map((b) => <SelectItem key={b.id} value={String(b.id)}>{b.name} {b.id === bancoPrincipalId ? '(Principal)' : ''}</SelectItem>)}
                {bancos.length === 0 && <SelectItem value="0" disabled>Sin bancos cargados</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Forma de pago</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger className="text-sm" data-testid="select-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="transfer">Transferencia</SelectItem>
                <SelectItem value="check">Cheque</SelectItem>
                <SelectItem value="cash">Efectivo</SelectItem>
                <SelectItem value="deposit">Depósito</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.payment_method === 'check' && (
            <div className="col-span-2">
              <Label className="text-xs">N° de cheque * (código de conciliación = C + n° cheque)</Label>
              <Input placeholder="2455945" value={form.numero_cheque} onChange={(e) => setForm({ ...form, numero_cheque: e.target.value })} className="text-sm font-mono" data-testid="input-numero-cheque" />
            </div>
          )}
          <div className="col-span-2">
            <Label className="text-xs">Descripción</Label>
            <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={2} className="text-sm" placeholder={modo === 'anticipo' ? 'Anticipo 30% para OC...' : 'Glosa del egreso'} data-testid="input-descripcion" />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="crear-alegra" checked={form.crear_en_alegra} onChange={(e) => setForm({ ...form, crear_en_alegra: e.target.checked })} data-testid="check-crear-alegra" />
            <Label htmlFor="crear-alegra" className="text-xs cursor-pointer">Crear también en Alegra</Label>
          </div>
        </div>
        {preview && (
          <Alert className="border-sky-200 bg-sky-50/50" data-testid="preview-conciliacion">
            <AlertCircle className="h-4 w-4 text-sky-600" />
            <AlertDescription className="text-xs text-sky-900">
              Fecha esperada en cartola: <b>{fmtFecha(preview.fecha_cartola_esperada)}</b> · Código de conciliación: <b className="font-mono">{preview.codigo}</b>
              {!preview.es_habil_bancario && ' (la fecha de pago es inhábil bancario)'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancelar</Button>
          <Button onClick={handleCreate} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-confirm-crear">
            {submitting ? 'Guardando…' : 'Crear egreso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
